import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/Skeleton';
import { ru } from '@/i18n/ru';
import { cn } from '@/lib/cn';
import { libraryApi, type FolderChild } from '@/lib/endpoints';

function ancestorPaths(path: string): string[] {
  if (!path) return [];
  const parts = path.split('/').filter(Boolean);
  const out: string[] = [];
  let acc = '';
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : part;
    out.push(acc);
  }
  return out;
}

interface FolderTreeProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

function FolderNode({
  folder,
  depth,
  currentPath,
  onNavigate,
  expanded,
  onToggle,
}: {
  folder: FolderChild;
  depth: number;
  currentPath: string;
  onNavigate: (path: string) => void;
  expanded: Set<string>;
  onToggle: (path: string) => void;
}) {
  const isOpen = expanded.has(folder.path);
  const isActive = currentPath === folder.path || currentPath.startsWith(`${folder.path}/`);
  const isExact = currentPath === folder.path;

  const childrenQuery = useQuery({
    queryKey: ['folders', folder.path],
    queryFn: () => libraryApi.folders(folder.path),
    enabled: isOpen,
    staleTime: 30_000,
  });

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-0.5 h-7 pr-2 text-xs cursor-pointer',
          isExact ? 'bg-row-active text-text' : 'text-text-muted hover:bg-row-hover hover:text-text',
        )}
        style={{ paddingLeft: 6 + depth * 12 }}
      >
        <button
          type="button"
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-text-muted hover:text-text"
          aria-label={isOpen ? ru.collapse : ru.expand}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(folder.path);
          }}
        >
          <ChevronRight
            className={cn('h-3 w-3 transition-transform duration-100', isOpen && 'rotate-90')}
          />
        </button>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          onClick={() => onNavigate(folder.path)}
        >
          {isOpen || isActive ? (
            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-accent" />
          ) : (
            <Folder className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate">{folder.name}</span>
          <span className="ml-auto shrink-0 font-mono text-[10px] text-text-muted tabular-nums opacity-70">
            {folder.fileCount}
          </span>
        </button>
      </div>
      {isOpen ? (
        <div>
          {childrenQuery.isLoading ? (
            <div className="space-y-1 py-1" style={{ paddingLeft: 18 + depth * 12 }}>
              <Skeleton className="h-5 w-4/5" />
              <Skeleton className="h-5 w-3/5" />
            </div>
          ) : null}
          {childrenQuery.data?.folders.map((child) => (
            <FolderNode
              key={child.path}
              folder={child}
              depth={depth + 1}
              currentPath={currentPath}
              onNavigate={onNavigate}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function FolderTree({ currentPath, onNavigate }: FolderTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(ancestorPaths(currentPath)),
  );

  useEffect(() => {
    const ancestors = ancestorPaths(currentPath);
    if (!ancestors.length) return;
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const p of ancestors) {
        if (!next.has(p)) {
          next.add(p);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [currentPath]);

  const rootQuery = useQuery({
    queryKey: ['folders', ''],
    queryFn: () => libraryApi.folders(''),
    staleTime: 30_000,
  });

  const onToggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  return (
    <nav className="h-full flex flex-col border-r border-border bg-surface/60" aria-label={ru.folders}>
      <div className="h-[var(--topbar-h)] shrink-0 flex items-center gap-2 px-3 border-b border-border">
        <Folder className="h-3.5 w-3.5 text-accent" />
        <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-text">
          {ru.root}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        <button
          type="button"
          onClick={() => onNavigate('')}
          className={cn(
            'flex w-full items-center gap-1.5 h-7 px-3 text-xs',
            currentPath === ''
              ? 'bg-row-active text-text'
              : 'text-text-muted hover:bg-row-hover hover:text-text',
          )}
        >
          <Folder className="h-3.5 w-3.5" />
          <span>{ru.root}</span>
        </button>
        {rootQuery.isLoading ? (
          <div className="space-y-1 px-3 py-2">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-5 w-3/5" />
          </div>
        ) : null}
        {rootQuery.isError ? (
          <p className="px-3 py-2 text-[11px] text-danger">{ru.foldersLoadFailed}</p>
        ) : null}
        {rootQuery.data?.folders.map((folder) => (
          <FolderNode
            key={folder.path}
            folder={folder}
            depth={0}
            currentPath={currentPath}
            onNavigate={onNavigate}
            expanded={expanded}
            onToggle={onToggle}
          />
        ))}
      </div>
    </nav>
  );
}
